import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import AccountsSubNav from '../components/AccountsSubNav';
import { appToast } from '../../../../components/AppToast';
import {
  AccountDetailsCard,
  BankDetailsCard,
  ContactInfoCard,
  TaxationCard,
  OrgStructureCheckboxPanel,
} from '../../../../components/shared-details';
import {
  getEmployeeDetail,
  createEmployee,
  updateEmployee,
} from '../../../payroll/employee/api/employeeApi';
import api from '../../../../api/client';

export default function EmployeeEntryScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { id: paramId } = useParams();
  const id = paramId || searchParams.get('id');
  const isEdit = Boolean(id);
  const isPayroll = location.pathname.startsWith('/payroll');
  const listPath = isPayroll ? '/payroll/employee' : '/masters/acheads/employees/list';

  const [form, setForm] = useState({
    name: '',
    group: 'Employees',
    alias: '',
    place: '',
    cost_center: 0,
    budget: 0, // Maps to CTC/Hr.
    amount: 0,
    balance_type: 'Cr',
    hsn_sac: '',
    agent: '', // Maps to Reporting To
    remarks: '',
    companies: [],
    branches: [],
    departments: [],
    status: 'Active',
    // Contact Info
    custGroup: 'Select', // Maps to Emp. Group
    contactPerson: '',
    designation: '',
    emailId: '',
    ccEmailId: '',
    billTo: '',
    shipTo: '',
    mobile: '',
    phone: '',
    contactInfo: '',
    creditDays: 0,
    interestRate: 0, // Maps to Commn. %
    interestPeriod: 'Year',
    birthDate: '',
    anniversary: '',
    // Bank Details
    bank: '',
    bankBranch: '',
    acNo: '',
    micrCo: '',
    ifscCode: '',
    bankType: 'Savings',
    minPdc: 0,
    // Taxation
    taxType: 'Regular',
    taxState: 'Maharashtra',
    transporter: 'Select',
    pan: '',
    tds: 'Select',
    tdsCode: 'Select',
    aadhaarNo: '',
    tcs: '0%',
    msmeType: 'Select',
    taxCategory: 'Select',
    gta: 0,
    gst: '',
    pincode: '',
    distance: 0,
    tan: '',
    tdsRate: 0,
    resident: 'Select',
    cinNo: '',
    msmeNo: '',
    currency: 'INR',
    ledger: 'None',
  });

  const [reportingToOptions, setReportingToOptions] = useState([
    { value: 'Select', label: 'Select' },
  ]);

  const [empGroupOptions, setEmpGroupOptions] = useState([
    { value: 'Select', label: 'Select' },
    { value: 'TEACHER', label: 'TEACHER' },
    { value: 'COUNSELLOR', label: 'COUNSELLOR' },
    { value: 'HR', label: 'HR' },
    { value: 'Admin', label: 'Admin' },
    { value: 'SUPPORT', label: 'SUPPORT' },
  ]);

  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Load options for Reporting To and Emp Groups
  useEffect(() => {
    let isMounted = true;
    const fetchOptions = async () => {
      try {
        const [empRes, grpRes] = await Promise.all([
          api.get('/options/employees').catch(() => ({ data: [] })),
          api.get('/options/employee-groups').catch(() => ({ data: [] })),
        ]);
        if (!isMounted) return;

        if (Array.isArray(empRes.data)) {
          setReportingToOptions([
            { value: 'Select', label: 'Select' },
            ...empRes.data.map((e) => ({
              value: String(e.id),
              label: `${e.name}${e.code ? ` (${e.code})` : ''}`,
            })),
          ]);
        }

        if (Array.isArray(grpRes.data) && grpRes.data.length > 0) {
          setEmpGroupOptions([
            { value: 'Select', label: 'Select' },
            ...grpRes.data.map((g) => ({
              value: String(g.id),
              label: g.name,
            })),
          ]);
        }
      } catch (err) {
        console.error('Failed to load employee options', err);
      }
    };
    fetchOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch employee details when in edit mode
  useEffect(() => {
    if (isEdit && id) {
      setIsLoading(true);
      getEmployeeDetail(id)
        .then((emp) => {
          setForm({
            name: emp.name || '',
            group: 'Employees',
            alias: emp.code || '',
            place: emp.city || '',
            cost_center: emp.cost_center ? 1 : 0,
            budget: Number(emp.budget || 0),
            amount: 0,
            balance_type: 'Cr',
            hsn_sac: emp.hsn_sac || '',
            agent: emp.reporting_to ? String(emp.reporting_to) : 'Select',
            remarks: emp.remarks || '',
            companies: emp.company_ids || [],
            branches: emp.branch_ids || [],
            departments: emp.department_ids || [],
            status: emp.archive ? 'Archive' : 'Active',
            // Contact Info
            custGroup: emp.employee_group_id ? String(emp.employee_group_id) : 'Select',
            contactPerson: emp.contact_person || '',
            designation: emp.designation || '',
            emailId: emp.email || '',
            ccEmailId: emp.cc_email || '',
            billTo: emp.bill_to || emp.address_line1 || '',
            shipTo: emp.ship_to || '',
            mobile: emp.mobile || '',
            phone: emp.phone || '',
            contactInfo: emp.contact_info || '',
            creditDays: Number(emp.credit_days || 0),
            interestRate: Number(emp.interest_rate || 0),
            interestPeriod: emp.interest_period || 'Year',
            birthDate: emp.birth_date || '',
            anniversary: emp.anniversary || '',
            // Bank Details
            bank: emp.bank_name || '',
            bankBranch: emp.bank_branch || '',
            acNo: emp.bank_ac_no || '',
            micrCo: emp.micr_code || '',
            ifscCode: emp.bank_ifsc || '',
            bankType: emp.bank_ac_type || 'Savings',
            minPdc: Number(emp.min_pdc || 0),
            // Taxation
            taxType: emp.tax_type || 'Regular',
            taxState: emp.tax_state || emp.state || 'Maharashtra',
            transporter: emp.transporter || 'Select',
            pan: emp.pan || '',
            tds: emp.tds || 'Select',
            tdsCode: emp.tds_code || 'Select',
            aadhaarNo: emp.aadhar_no || '',
            tcs: emp.tcs || '0%',
            msmeType: emp.msme_type || 'Select',
            taxCategory: emp.tax_category || 'Select',
            gta: emp.is_gta ? 1 : 0,
            gst: emp.gst || '',
            pincode: emp.pincode || '',
            distance: Number(emp.distance || 0),
            tan: emp.tan || '',
            tdsRate: Number(emp.tds_rate || 0),
            resident: emp.resident || 'Select',
            cinNo: emp.cin_no || '',
            msmeNo: emp.msme_no || '',
            currency: emp.currency || 'INR',
            ledger: emp.ledger || 'None',
          });
        })
        .catch((err) => {
          console.error('Failed to fetch employee details', err);
          appToast.error('Failed to load employee details.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [id, isEdit]);

  const patchForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async (options = { exitAfter: true }) => {
    if (!form.name.trim()) {
      appToast.error('Employee Name is required.');
      return;
    }

    setIsSaving(true);

    const payload = {
      name: form.name.trim(),
      code: form.alias ? form.alias.trim() : '',
      group_id: 33,
      remarks: form.remarks ? form.remarks.trim() : '',
      budget: Number(form.budget || 0),
      cost_center: Boolean(form.cost_center),
      hsn_sac: form.hsn_sac ? form.hsn_sac.trim() : null,
      reporting_to: form.agent && form.agent !== 'Select' && !isNaN(form.agent) ? Number(form.agent) : null,
      company_ids: (form.companies || []).map(Number).filter((n) => n > 0),
      branch_ids: (form.branches || []).map(Number).filter((n) => n > 0),
      department_ids: (form.departments || []).map(Number).filter((n) => n > 0),
      employee_group_id: form.custGroup && form.custGroup !== 'Select' && !isNaN(form.custGroup) ? Number(form.custGroup) : null,
      contact_person: form.contactPerson ? form.contactPerson.trim() : null,
      designation: form.designation ? form.designation.trim() : null,
      email: form.emailId ? form.emailId.trim() : null,
      cc_email: form.ccEmailId ? form.ccEmailId.trim() : null,
      address_line1: form.billTo ? form.billTo.trim() : null,
      bill_to: form.billTo ? form.billTo.trim() : null,
      ship_to: form.shipTo ? form.shipTo.trim() : null,
      mobile: form.mobile ? form.mobile.trim() : null,
      phone: form.phone ? form.phone.trim() : null,
      contact_info: form.contactInfo ? form.contactInfo.trim() : null,
      credit_days: Number(form.creditDays || 0),
      interest_rate: Number(form.interestRate || 0),
      interest_period: form.interestPeriod || 'Year',
      birth_date: form.birthDate ? form.birthDate : null,
      anniversary: form.anniversary ? form.anniversary : null,
      bank_name: form.bank ? form.bank.trim() : null,
      bank_branch: form.bankBranch ? form.bankBranch.trim() : null,
      bank_ac_no: form.acNo ? form.acNo.trim() : null,
      bank_ifsc: form.ifscCode ? form.ifscCode.trim() : null,
      micr_code: form.micrCo ? form.micrCo.trim() : null,
      bank_ac_type: form.bankType || 'Savings',
      min_pdc: Number(form.minPdc || 0),
      tax_type: form.taxType || 'Regular',
      state: form.taxState || 'Maharashtra',
      tax_state: form.taxState || 'Maharashtra',
      transporter: form.transporter !== 'Select' ? form.transporter : null,
      pan: form.pan ? form.pan.trim() : null,
      tds: form.tds !== 'Select' ? form.tds : null,
      tds_code: form.tdsCode !== 'Select' ? form.tdsCode : null,
      aadhar_no: form.aadhaarNo ? form.aadhaarNo.trim() : null,
      tcs: form.tcs || '0%',
      msme_type: form.msmeType !== 'Select' ? form.msmeType : null,
      tax_category: form.taxCategory !== 'Select' ? form.taxCategory : null,
      is_gta: Boolean(form.gta),
      gst: form.gst ? form.gst.trim() : null,
      pincode: form.pincode ? form.pincode.trim() : null,
      distance: Number(form.distance || 0),
      tan: form.tan ? form.tan.trim() : null,
      tds_rate: Number(form.tdsRate || 0),
      resident: form.resident !== 'Select' ? form.resident : null,
      cin_no: form.cinNo ? form.cinNo.trim() : null,
      msme_no: form.msmeNo ? form.msmeNo.trim() : null,
      currency: form.currency || 'INR',
      ledger: form.ledger || 'None',
      city: form.place ? form.place.trim() : null,
    };

    try {
      if (isEdit) {
        await updateEmployee(id, payload);
        appToast.success('Employee updated successfully!');
      } else {
        const res = await createEmployee(payload);
        appToast.success('Employee created successfully!');
        if (!options.exitAfter && res?.id) {
          navigate(
            `${isPayroll ? '/payroll/employee/entry' : '/masters/acheads/employees/entry'}?id=${res.id}`,
            { replace: true }
          );
        }
      }

      if (options.exitAfter) {
        setTimeout(() => {
          navigate(listPath);
        }, 350);
      } else if (!isEdit) {
        setForm({
          ...form,
          name: '',
          alias: '',
          place: '',
          mobile: '',
          emailId: '',
          bank: '',
          acNo: '',
        });
      }
    } catch (err) {
      console.error('Failed to save Employee', err);
      const msg = err.response?.data?.detail || 'Failed to save Employee.';
      appToast.error(typeof msg === 'string' ? msg : JSON.stringify(msg));
    } finally {
      setIsSaving(false);
    }
  };

  // Component Values
  const accountDetailsValue = {
    acGroup: form.group,
    acName: form.name,
    alias: form.alias,
    place: form.place,
    costCenter: Boolean(form.cost_center),
    budget: form.budget, // CTC/Hr.
    hsnSac: form.hsn_sac,
    agent: form.agent, // Reporting To
    remarks: form.remarks,
  };

  const handleAccountDetailsChange = (vals) => {
    patchForm('group', vals.acGroup || '');
    patchForm('name', vals.acName || '');
    patchForm('alias', vals.alias || '');
    patchForm('place', vals.place || '');
    patchForm('cost_center', vals.costCenter ? 1 : 0);
    patchForm('budget', Number(vals.budget || 0));
    patchForm('hsn_sac', vals.hsnSac || '');
    patchForm('agent', vals.agent || '');
    patchForm('remarks', vals.remarks || '');
  };

  const bankDetailsValue = {
    bank: form.bank,
    branch: form.bankBranch,
    acNo: form.acNo,
    micrCo: form.micrCo,
    ifscCode: form.ifscCode,
    type: form.bankType,
    minPdc: form.minPdc,
  };

  const handleBankDetailsChange = (vals) => {
    patchForm('bank', vals.bank || '');
    patchForm('bankBranch', vals.branch || '');
    patchForm('acNo', vals.acNo || '');
    patchForm('micrCo', vals.micrCo || '');
    patchForm('ifscCode', vals.ifscCode || '');
    patchForm('bankType', vals.type || 'Savings');
    patchForm('minPdc', Number(vals.minPdc || 0));
  };

  const contactInfoValue = {
    custGroup: form.custGroup, // Emp. Group
    person: form.contactPerson,
    designation: form.designation,
    emailId: form.emailId,
    ccEmailId: form.ccEmailId,
    billTo: form.billTo,
    shipTo: form.shipTo,
    mobile: form.mobile,
    phone: form.phone,
    contactInfo: form.contactInfo,
    creditDays: form.creditDays,
    interestRate: form.interestRate, // Commn. %
    interestPeriod: form.interestPeriod,
    birthDate: form.birthDate,
    anniversary: form.anniversary,
  };

  const handleContactInfoChange = (vals) => {
    patchForm('custGroup', vals.custGroup || 'Select');
    patchForm('contactPerson', vals.person || '');
    patchForm('designation', vals.designation || '');
    patchForm('emailId', vals.emailId || '');
    patchForm('ccEmailId', vals.ccEmailId || '');
    patchForm('billTo', vals.billTo || '');
    patchForm('shipTo', vals.shipTo || '');
    patchForm('mobile', vals.mobile || '');
    patchForm('phone', vals.phone || '');
    patchForm('contactInfo', vals.contactInfo || '');
    patchForm('creditDays', Number(vals.creditDays || 0));
    patchForm('interestRate', Number(vals.interestRate || 0));
    patchForm('interestPeriod', vals.interestPeriod || 'Year');
    patchForm('birthDate', vals.birthDate || '');
    patchForm('anniversary', vals.anniversary || '');
  };

  const taxationValue = {
    type: form.taxType,
    state: form.taxState,
    transporter: form.transporter,
    pan: form.pan,
    tds: form.tds,
    tdsCode: form.tdsCode,
    aadhaarNo: form.aadhaarNo,
    tcs: form.tcs,
    msmeType: form.msmeType,
    category: form.taxCategory,
    gta: Boolean(form.gta),
    gst: form.gst,
    pincode: form.pincode,
    distance: form.distance,
    tan: form.tan,
    tdsRate: form.tdsRate,
    resident: form.resident,
    cinNo: form.cinNo,
    msmeNo: form.msmeNo,
    currency: form.currency,
    ledger: form.ledger,
  };

  const handleTaxationChange = (vals) => {
    patchForm('taxType', vals.type || 'Regular');
    patchForm('taxState', vals.state || 'Maharashtra');
    patchForm('transporter', vals.transporter || 'Select');
    patchForm('pan', vals.pan || '');
    patchForm('tds', vals.tds || 'Select');
    patchForm('tdsCode', vals.tdsCode || 'Select');
    patchForm('aadhaarNo', vals.aadhaarNo || '');
    patchForm('tcs', vals.tcs || '0%');
    patchForm('msmeType', vals.msmeType || 'Select');
    patchForm('taxCategory', vals.category || 'Select');
    patchForm('gta', vals.gta ? 1 : 0);
    patchForm('gst', vals.gst || '');
    patchForm('pincode', vals.pincode || '');
    patchForm('distance', Number(vals.distance || 0));
    patchForm('tan', vals.tan || '');
    patchForm('tdsRate', Number(vals.tdsRate || 0));
    patchForm('resident', vals.resident || 'Select');
    patchForm('cinNo', vals.cinNo || '');
    patchForm('msmeNo', vals.msmeNo || '');
    patchForm('currency', vals.currency || 'INR');
    patchForm('ledger', vals.ledger || 'None');
  };

  const groupOptions = [{ value: 'Employees', label: 'Employees' }];

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-16">
      {/* Header */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">
            {isPayroll ? 'Payroll Suite' : 'Financial Master'}
          </p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            {isEdit ? 'Edit Employee' : 'Create Employee'}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {!isPayroll && <AccountsSubNav />}
          <Link
            to={listPath}
            className="inline-flex items-center gap-1 text-xs font-bold text-on-surface-variant hover:text-primary transition"
          >
            <span className="material-symbols-outlined text-base">arrow_back</span>
            <span>Back</span>
          </Link>
        </div>
      </section>

      {isLoading ? (
        <div className="flex items-center justify-center p-12 text-sm text-on-surface-variant font-medium">
          Loading employee details...
        </div>
      ) : (
        <>
          {/* Two-Column Details Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            {/* Left Column */}
            <div className="flex flex-col gap-6">
              <AccountDetailsCard
                value={accountDetailsValue}
                onChange={handleAccountDetailsChange}
                groupOptions={groupOptions}
                agentOptions={reportingToOptions}
                budgetLabel="CTC/Hr."
                agentLabel="Reporting To"
                onAddPlaceClick={() => appToast.info('Place master integration is under construction.')}
              />
              <BankDetailsCard
                value={bankDetailsValue}
                onChange={handleBankDetailsChange}
              />
            </div>

            {/* Right Column */}
            <div className="flex flex-col gap-6">
              <ContactInfoCard
                value={contactInfoValue}
                onChange={handleContactInfoChange}
                groupOptions={empGroupOptions}
                groupLabel="Emp. Group"
                interestLabel="Commn."
              />
              <TaxationCard
                value={taxationValue}
                onChange={handleTaxationChange}
                onGstVerifyClick={() => appToast.info('Verifying GST details with GSTN portal...')}
              />
            </div>
          </div>

          {/* Full Width Bottom Organization Panels */}
          <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest p-6 shadow-sm">
            <div className="border-b border-outline-variant/20 pb-4 mb-6">
              <h2 className="font-display text-xl font-bold text-on-surface">Organization Scope</h2>
              <p className="text-xs text-on-surface-variant">
                Assign the Companies, Branches, and Departments where this Employee is authorized.
              </p>
            </div>
            <OrgStructureCheckboxPanel
              selectedCompanies={form.companies}
              selectedBranches={form.branches}
              selectedDepartments={form.departments}
              onCompaniesChange={(val) => patchForm('companies', val)}
              onBranchesChange={(val) => patchForm('branches', val)}
              onDepartmentsChange={(val) => patchForm('departments', val)}
            />
          </section>

          {/* Footer Actions Bar */}
          <section className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-4 shadow-sm">
            <button
              type="button"
              onClick={() => appToast.info('Audit history tracking is enabled.')}
              className="inline-flex items-center gap-1.5 rounded-xl bg-surface-container-low px-4 py-2.5 text-xs font-bold text-on-surface-variant transition hover:bg-surface-container-high"
            >
              <span className="material-symbols-outlined text-sm">history</span>
              <span>History</span>
            </button>

            <div className="flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={() => navigate(listPath)}
                className="rounded-xl bg-secondary-container px-6 py-2.5 text-xs font-bold text-on-secondary-container transition hover:brightness-95 cursor-pointer"
              >
                Cancel
              </button>

              {!isEdit && (
                <button
                  type="button"
                  disabled={isSaving}
                  onClick={() => handleSave({ exitAfter: false })}
                  className="rounded-xl border border-outline-variant/40 bg-surface-container-lowest px-6 py-2.5 text-xs font-bold text-primary transition hover:bg-surface-container-low disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
                >
                  Save &amp; Continue
                </button>
              )}

              <button
                type="button"
                disabled={isSaving}
                onClick={() => handleSave({ exitAfter: true })}
                className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-br from-primary to-primary-container px-8 py-2.5 text-xs font-bold text-on-primary shadow-lg shadow-primary/20 transition hover:brightness-105 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer"
              >
                <span className="material-symbols-outlined text-base">save</span>
                <span>{isSaving ? 'Saving...' : 'Save & Exit'}</span>
              </button>
            </div>
          </section>
        </>
      )}
    </div>
  );
}
